import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { Platform } from '@app/classes';
import { StepsComponent } from '@app/components';
import { CompensationTypes } from '@app/constants/compensation-types';
import { AnalyticEvents } from '@app/enums';
import { RoleAssignment } from '@app/models/account/role-assignment.model';
import { Role } from '@app/models/account/role.model';
import { Address } from '@app/models/employee/address.model';
import { AbilityService, AnalyticService, AuthService, NotifyService } from '@app/services';
import { StoreView } from '@common/views';
import { TerminationService } from '@employees/services/termination.service';
import { Account } from '@models/account/account.model';
import { Office } from '@models/company/office.model';
import { Employee } from '@models/employee/employee.model';
import { Salary } from '@models/employee/salary.model';
import moment from 'moment';

@Component({
    templateUrl: './terminated-employee-store.template.html',
    styleUrls: ['./terminated-employee-store.style.scss'],
})
export class TerminatedEmployeeStoreView extends StoreView implements AfterViewInit {
    @ViewChild('steps', { static: true }) steps: StepsComponent;
    employee: Employee = new Employee({
        status: 'onboarding',
        isPayrollSyncEnabled: false,
        collectHiringQuestions: false,
        wasAddedByTerminationFlow: true,
    });
    account: Account = new Account({ isLocked: true, isActive: false });
    salary: Salary = new Salary({
        isPrimary: true,
    });
    address: Address = new Address();
    canDoPayroll = false;
    terminatedOn;

    offices: Office[] = [];
    isLoading = false;

    private employeeRoleId: number;

    constructor(
        protected element: ElementRef,
        private router: Router,
        private auth: AuthService,
        private notify: NotifyService,
        private abilities: AbilityService,
        private terminationService: TerminationService,
        private analyticService: AnalyticService
    ) {
        super();
        this.canDoPayroll = this.abilities.payroll() && this.auth.can(Platform.payroll.permission.view);

        this.isLoading = true;
        Promise.all([this.getOffices(), this.getEmployeeRole()])
            .then(() => {
                this.isLoading = false;
            })
            .catch((err) => {
                this.notify.error(err);
                this.isLoading = false;
            });
    }

    ngAfterViewInit(): void {
        this.focusInput();
    }

    focusInput(): void {
        setTimeout(() => {
            const input: HTMLInputElement | undefined = this.element.nativeElement.querySelector('input');
            // Don't want to autofocus dropdowns
            if (input?.matches('.search')) {
                return;
            }

            input?.focus();
        });
    }

    onSave(): void {
        /* Assume the entered employee name is also the legal name used for the account */
        this.account.legalFirstName = this.employee.firstName;
        this.account.legalLastName = this.employee.lastName;

        this.isLoading = true;

        this.saveAccount()
            .then(() => this.saveEmployee())
            .then(() => this.associateRole())
            .then(() => this.saveAddress())
            .then(() => this.saveSalary())
            .then(() => this.terminateEmployee())
            .then(() => {
                this.analyticService.trackEvent(AnalyticEvents.CreateTerminatedEmployee);
                this.isLoading = false;
                this.notify.success('forms.add-terminated.successToast');
                this.router.navigate(['employees', 'directory']);
            })
            .catch((err) => {
                this.resetEmployee(); // reset employee so the flow can be tried again.
                this.notify.error(err);
                this.isLoading = false;
            });
    }

    private async terminateEmployee(): Promise<void> {
        try {
            await this.terminationService.terminate(
                this.employee,
                moment(this.employee.lastDayOfWorkOn).toDate(),
                this.employee.terminationReasonCode,
                this.employee.terminationComments
            );
        } catch (e) {
            this.notify.error(e);
        }
    }

    private resetEmployee(): void {
        this.employee.status = 'onboarding';
        this.employee.terminatedOn = null;
    }

    private async saveAccount(): Promise<any> {
        const account = await this.account.save();
        this.account = account;
        this.employee.accountId = this.account.id;
    }

    private async saveEmployee(): Promise<any> {
        this.employee.status = 'terminated';
        this.employee.terminatedOn = this.terminatedOn;
        const employee = await this.employee.param('company', this.auth.company.id).save();

        return (this.employee = employee);
    }

    private saveAddress(): Promise<any> {
        return this.address.param('company', this.auth.company.id).param('employee', this.employee.id).save();
    }

    private shouldNotPersistSalary(): boolean {
        return (
            this.salary.isPersisted ||
            (!CompensationTypes.hasOwnProperty(this.salary.frequency) && !this.salary.rate && !this.salary.effectiveAt)
        );
    }

    private saveSalary(): Promise<any> {
        if (this.shouldNotPersistSalary()) {
            return Promise.resolve();
        }

        this.salary.employeeId = this.employee.id;
        return this.salary.param('company', this.auth.company.id).param('employee', this.employee.id).save();
    }

    private associateRole(): Promise<any> {
        return new RoleAssignment({ roleId: this.employeeRoleId }).param('account', this.account.id).save();
    }

    private async getOffices(): Promise<any> {
        const [offices] = await Office.param('company', this.auth.company.id).all();
        this.offices = offices;
        this.isLoading = false;
    }

    private async getEmployeeRole(): Promise<any> {
        const [roles] = await Role.where('company_id', this.auth.company.id)
            .where('name', 'Employee')
            .where('is_immutable', 1)
            .get();
        // A company will only have 1 role with name Employee that is immutable
        this.employeeRoleId = roles[0].id;
    }
}
